<template>
  <div class="quote-validation-page">
    <b-container class="mt-3">
      <b-row class="justify-content-center">
        <b-col cols="12" md="10" lg="8">
          <!-- Header -->
          <div class="text-center mb-3">
            <h1 class="text-primary mb-0">{{ currAppName }}</h1>
            <p class="text-muted">Validation de devis en ligne</p>
          </div>

          <!-- Alert Error -->
          <b-alert v-if="errorMessage" variant="danger" show dismissible @dismissed="errorMessage = null">
            <div class="alert-body">
              <feather-icon icon="AlertCircleIcon" class="mr-50" />
              {{ errorMessage }}
            </div>
          </b-alert>

          <!-- Card Container -->
          <b-card>
            <!-- Step 1: OTP -->
            <quote-validation-step-otp
              v-if="currentStep === 1"
              :is-loading="isLoading"
              :error-message="validationError"
              @verify="handleVerifyCode"
            />

            <!-- Step 2: Review -->
            <quote-validation-step-review
              v-if="currentStep === 2"
              :budget-tracking="budgetTracking"
              :opportunity-reference="opportunityReference"
              :is-loading="isLoading"
              @approve="handleApprove"
              @reject="handleReject"
            />

            <!-- Step 3: Confirmation -->
            <quote-validation-step-confirmation
              v-if="currentStep === 3"
              :title="confirmationTitle"
              :message="confirmationMessage"
              :show-payment-info="showPaymentInfo"
            />
          </b-card>

          <!-- Footer -->
          <div class="text-center mt-2">
            <p class="text-muted small mb-0">
              © {{ new Date().getFullYear() }} {{ currAppName }} - Tous droits réservés
            </p>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { BContainer, BRow, BCol, BCard, BAlert } from "bootstrap-vue";
import { ref, computed } from "@vue/composition-api";
import { useRouter } from "@core/utils/utils";
import { $themeConfig } from '@themeConfig'
import useQuoteValidationPublic from "@/composables/useQuoteValidationPublic";
import QuoteValidationStepOtp from "./QuoteValidationStepOtp.vue";
import QuoteValidationStepReview from "./QuoteValidationStepReview.vue";
import QuoteValidationStepConfirmation from "./QuoteValidationStepConfirmation.vue";

export default {
  name: "QuoteValidation",
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BAlert,
    QuoteValidationStepOtp,
    QuoteValidationStepReview,
    QuoteValidationStepConfirmation,
  },
  setup() {
    const { route } = useRouter();
    const token = computed(() => route.value.params.token);

    // App Name
    const { appName } = $themeConfig.app

    const currAppName = computed(() => {
      let companyInfo = JSON.parse(localStorage.getItem('COMPANY_INFO'))
      if(companyInfo !== null) return companyInfo.name
      return appName
    })

    const {
      isLoading,
      budgetTracking,
      opportunityReference,
      errorMessage: validationError,
      verifyCode,
      approve,
      reject,
    } = useQuoteValidationPublic();

    const currentStep = ref(1);
    const errorMessage = ref(null);
    const confirmationTitle = ref("");
    const confirmationMessage = ref("");
    const showPaymentInfo = ref(false);

    const handleVerifyCode = async (code) => {
      try {
        await verifyCode(token.value, code);
        currentStep.value = 2;
        errorMessage.value = null;
      } catch (error) {
        errorMessage.value =
          error.response?.data?.message || "Code invalide ou expiré";
      }
    };

    const handleApprove = async () => {
      try {
        const response = await approve(token.value);
        confirmationTitle.value = "Devis approuvé";
        confirmationMessage.value =
          response.message ||
          "Votre devis a été approuvé avec succès. Un projet a été généré automatiquement.";
        showPaymentInfo.value = true;
        currentStep.value = 3;
        errorMessage.value = null;
      } catch (error) {
        errorMessage.value =
          error.response?.data?.message || "Erreur lors de l'approbation du devis";
      }
    };

    const handleReject = async (comment) => {
      try {
        const response = await reject(token.value, comment);
        confirmationTitle.value = "Devis rejeté";
        confirmationMessage.value =
          response.message ||
          "Votre refus a été enregistré. Le commercial sera notifié de votre décision.";
        showPaymentInfo.value = false;
        currentStep.value = 3;
        errorMessage.value = null;
      } catch (error) {
        errorMessage.value =
          error.response?.data?.message || "Erreur lors du rejet du devis";
      }
    };

    return {
      token,
      currAppName,
      currentStep,
      isLoading,
      budgetTracking,
      opportunityReference,
      validationError,
      errorMessage,
      confirmationTitle,
      confirmationMessage,
      showPaymentInfo,
      handleVerifyCode,
      handleApprove,
      handleReject,
    };
  },
};
</script>

<style lang="scss" scoped>
.quote-validation-page {
  min-height: 100vh;
  background: ghostwhite;
  padding: 2rem 0;

  @media (max-width: 768px) {
    padding: 1rem 0;
  }
}
</style>
